/**
 * TitleBlockCover — the SIGNAL title-block cover (engineering / advisory brief).
 *
 * The redesign-v2 SIGNAL register (`.t-signal` / `.sg-*` in the mockup) reads
 * like a drawing-sheet title block: a faint GRAPH-PAPER LATTICE behind the
 * page, a strong top rule with an eyebrow + a small square brandmark, the
 * project NAME in the display grotesque (bold uppercase), a one-line verdict
 * (accent on the decision word), then an engineering TITLE-BLOCK GRID of fielded
 * cells (PROJECT / LOCATION / SCOPE / CAPACITY / REV / DATE) with mono key + mono
 * value and ruled dividers, a small boundary-diagram inset, and a mono datum-strip
 * footer.
 *
 * RELATION TO THE OTHER COVERS: the data contract (`CoverProps`), the verdict
 * source (`getDisplayVerdict`), the KPI/field resolvers (`resolveCoverChipsForReport`
 * + the journal resolvers), the boundary inset (`HeroCoverBoundaryOverlay`, used
 * keyless exactly as BoundaryPlateCover / HeroImageCover use it), the
 * full-A4-clamp-minus-1mm-epsilon root (EditorialMastheadCover DEFECT 1), and the
 * on-cover TOC props (EditorialMastheadCover DEFECT 8) are all reused VERBATIM so
 * this cover stays in lock-step with its siblings. What differs is the editorial
 * register: a title-block drawing-sheet rather than a journal masthead.
 *
 * THEME-TOKEN DRIVEN: the mockup's blue/ink/paper hexes are the DEFAULT the
 * matching preset supplies — NONE are inlined. Every colour reads from the merged
 * `theme` (backgroundColor / headingColor / bodyTextColor) and via
 * `roleColor(theme, 'accent' | 'hair' | 'rule' | verdictRole)`, so selecting a
 * different preset reskins the whole cover. Fonts come from the theme
 * (displayFont → headingFont fallback / headingFont / bodyFont); data/mono labels
 * use 'IBM Plex Mono'.
 *
 * NULL-SAFE (NO DATA LOSS): every field resolver returns an em-dash rather than
 * throwing on a legacy report; an absent verdict collapses the verdict line; no
 * geometry collapses the boundary inset to a labelled empty plate (still keyless,
 * never blank-throwing); a near-empty index collapses the TOC (the <3-entry guard).
 */
import React from 'react';
import { PreparedForLine } from '../../PreparedForLine';
import {
  resolveCoverTitle,
  resolveCoverKicker,
  resolveSiteWordmark,
  resolveSiteAcres,
} from '../../../../lib/reportRendering/journalCoverData';
import { resolveCoverChipsForReport } from '../../../../lib/reportRendering/coverKpiChips';
import { HeroCoverBoundaryOverlay } from './HeroCoverBoundaryOverlay';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import { formatPageNumber, normalizePageNumberFormat } from '../../../../lib/reportLayoutTokens';
import { getReportType } from '../../../../config/reportTypes';
import { resolveCoverVerdict, type VerdictTier } from '../../../../lib/reportVerdict';
import { REPORT_COVER_DISCLAIMER } from '../../../../lib/reportLegalDisclaimer';
import type { CoverProps } from './types';
import { CoverHeaderPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/** Data/mono label font — the engineering datum voice (matches the mockup's
 *  `.sg-block .k` / `.sg-foot` IBM Plex Mono). Kept a literal here (not a theme
 *  token) because it IS the SIGNAL register's defining typographic move; every
 *  COLOUR around it still reads from the theme. */
const MONO_FONT = "'IBM Plex Mono', monospace";

/** Map a verdict tier → a role-colour key for the verdict-word accent + the
 *  brandmark/eyebrow tone. Mirrors EditorialMastheadCover.verdictStampRole. */
function verdictRoleForTier(tier: VerdictTier): 'positive' | 'warning' | 'negative' | 'muted' {
  switch (tier) {
    case 'green': return 'positive';
    case 'amber': return 'warning';
    case 'red': return 'negative';
    default: return 'muted';
  }
}

/**
 * Only render the resolved `disclaimer` prop block when it carries a DISTINCT
 * archetype override — otherwise the canonical beta caveat (rendered once in the
 * footer) would print twice. Mirrors EditorialMastheadCover.isDistinctDisclaimer.
 */
function isDistinctDisclaimer(disclaimer: string | undefined): disclaimer is string {
  if (typeof disclaimer !== 'string') return false;
  const trimmed = disclaimer.trim();
  return trimmed.length > 0 && trimmed !== REPORT_COVER_DISCLAIMER.trim();
}

/** "30.35, -93.25" location datum from the site centroid — null-safe. */
function resolveLocationDatum(report: CoverProps['report']): string | null {
  const c = report.siteCentroid;
  if (c && Number.isFinite(c.lat) && Number.isFinite(c.lng)) {
    return `${c.lat.toFixed(2)}, ${c.lng.toFixed(2)}`;
  }
  const addr = report.address?.trim();
  return addr && addr.length > 0 ? addr : null;
}

/** A short engineering project code, e.g. "DA-RIP-0003" — deterministic from the
 *  org series + site wordmark + report id, never thrown. */
function resolveProjectCode(report: CoverProps['report'], companyName: string | undefined): string {
  const brand = (companyName?.trim() || 'Datacrate')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'DA';
  const siteAbbr = (report.siteName?.trim() || 'SITE')
    .replace(/[^A-Za-z0-9]+/g, '')
    .slice(0, 3)
    .toUpperCase() || 'SITE';
  const tail = (report.id ?? '').replace(/[^A-Za-z0-9]+/g, '').slice(-4).toUpperCase() || '0001';
  return `${brand}-${siteAbbr}-${tail}`;
}

/** Scope cell: report short-label + phase count (e.g. "Power · 4 phase"). */
function resolveScopeDatum(report: CoverProps['report']): string {
  const type = report.reportType === 'custom-brief'
    ? 'custom-brief'
    : (report.reportType ?? 'feasibility');
  const short = getReportType(type).shortLabel;
  const phaseCount = report.developmentPathway?.filter(
    (p) => typeof p?.phase === 'string' && p.phase.trim().length > 0,
  ).length ?? 0;
  return phaseCount > 0 ? `${short} · ${phaseCount} phase` : short;
}

/** Rev cell: "v{n}.{m} · {YYYY-MM}" from the persisted version chain + gen date. */
function resolveRevDatum(report: CoverProps['report']): string {
  const major = typeof report.version === 'number' && Number.isFinite(report.version) ? report.version : 1;
  const minor = typeof report.minorVersion === 'number' && Number.isFinite(report.minorVersion) ? report.minorVersion : 0;
  const ym = resolveGenYearMonth(report);
  return ym ? `v${major}.${minor} · ${ym}` : `v${major}.${minor}`;
}

/** YYYY-MM-DD report date for the DATE cell + footer datum (the persisted
 *  `createdAt` snapshot — byte-stable across view + print/export). Null-safe. */
function resolveGenDate(report: CoverProps['report']): string | null {
  const iso = report.createdAt;
  if (typeof iso !== 'string' || iso.trim().length === 0) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** YYYY-MM slice of the generation date (for the Rev cell). */
function resolveGenYearMonth(report: CoverProps['report']): string | null {
  const full = resolveGenDate(report);
  return full ? full.slice(0, 7) : null;
}

export const TitleBlockCover: React.FC<CoverProps> = ({
  report, theme, branding,
  isAdmin,
  isEditing, editedAddress, onAddressChange,
  pickerOpen, onTogglePicker, onSelectArchetype,
  disclaimer,
  tocEntries,
  tocPageNumbers,
  tocPageNumberFormat,
}) => {
  // Branding resolved for parity with the other covers. The SIGNAL title block
  // carries its identity through the eyebrow + brandmark + datum footer (not a
  // raster logo), so `effectiveBranding` is referenced but the logo is left
  // UNRENDERED by design (mirrors EditorialMastheadCover / DarkInstrumentCover).
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  void effectiveBranding;

  const title = resolveCoverTitle(report);
  const eyebrow = resolveCoverKicker(theme.companyName, report);

  // Verdict — canonical COVER resolver. The verdict WORD is the accented lead of
  // the one-line verdict (mockup `.sg-verdict b`), the remainder is the
  // qualitative gloss. For the executive summary this sources the gen-time
  // decision-rollup band (PG-3 SSOT) so the exec cover finally carries a verdict;
  // every other type delegates to getDisplayVerdict unchanged. Collapses cleanly
  // when there is no real verdict.
  const verdict = resolveCoverVerdict(report);
  const hasVerdict = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label !== '—';
  const verdictRole = verdictRoleForTier(verdict.tier);

  // The verdict gloss — the recommendation / btm-decision one-liner that follows
  // the accented verdict word. Null-safe; absent → the verdict line is just the
  // word (still meaningful as the headline call).
  const verdictGloss = (verdict.savedDecisionPending || verdict.savedDecisionCap || verdict.savedDecisionConflict)
    ? (verdict.savedDecisionConflict ?? verdict.label) :
    report.conclusion?.recommendation?.trim()
    || report.btmDecisionSummary?.trim()
    || report.conclusion?.btmDecisionSummary?.trim()
    || null;

  // ── Title-block grid cells (mockup `.sg-block`): mono key + mono value. The
  // CAPACITY cell reuses the canonical chip resolver so the headline number stays
  // unified with every other cover (no invented metric); the rest derive from the
  // report's own fields. Every value falls back to an em-dash, never a throw. ──
  const chips = resolveCoverChipsForReport(report, 'data-dense');
  const powerChip = chips.find((c) => c.id === 'power');
  const capacityValue = powerChip?.value ?? null;

  const acres = resolveSiteAcres(report);
  const locationDatum = resolveLocationDatum(report);
  const genDate = resolveGenDate(report);

  // The 6 fielded cells, in title-block order. LOCATION is the editable cell in
  // viewer edit mode (the SIGNAL block has no separate address line), mirroring
  // ClassicCover's inline address field.
  type Field = { id: string; key: string; value: string; editable?: boolean };
  const fields: Field[] = [
    { id: 'project', key: 'Project', value: resolveProjectCode(report, theme.companyName) },
    { id: 'location', key: 'Location', value: locationDatum ?? '–', editable: true },
    { id: 'scope', key: 'Scope', value: resolveScopeDatum(report) },
    { id: 'capacity', key: 'Capacity', value: capacityValue ?? '–' },
    { id: 'rev', key: 'Rev', value: resolveRevDatum(report) },
    { id: 'date', key: 'Date', value: genDate ?? '–' },
  ];

  // Boundary inset (mockup `.sg-svg`) — the SHARED keyless overlay, exactly as
  // BoundaryPlateCover / HeroImageCover mount it. No geometry → a labelled empty
  // datum line rather than a blank/throw.
  const layers = effectiveBranding.kmzLayers ?? [];
  const hasBoundary =
    (report.siteBoundaries?.length ?? 0) > 0 || (report.siteBoundary?.length ?? 0) > 0;
  const hasGeometry = hasBoundary || layers.length > 0;

  // DEFECT 8 (parity) — compact on-cover index threaded from the caller
  // (deriveTocEntries + exec-summary de-dup already applied). The <3-entry guard
  // mirrors EditorialMastheadCover so a near-empty index never renders. The
  // SIGNAL register draws it as a hairline title-block continuation grid.
  const carriesToc = Array.isArray(tocEntries) && tocEntries.length >= 3;
  const tocFmt = normalizePageNumberFormat(tocPageNumberFormat);

  const displayFont = theme.displayFont || theme.headingFont;
  // Title scale tracks the resolved h2 (headingScale + themeTypographyScale
  // aware) — a confident drawing-sheet headline, bounded so a long title still
  // fits the block. Mirrors the editorial covers' clamp idiom.
  const titleSize = `clamp(30px, 5vw, calc(${reportTypographySizes(theme).h2} * 2.4))`;
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;

  return (
    <div
      className="relative flex flex-col overflow-hidden"
      data-testid="cover-title-block"
      data-cover-variant="title-block"
      data-verdict-tier={verdict.tier}
      style={{
        backgroundColor: theme.backgroundColor,
        color: roleColor(theme, 'body'),
        // GRAPH-PAPER LATTICE — two repeating hairline gradients in a faint
        // theme-derived ink wash (mockup's faint blue grid). Kept subtle
        // (≈4.7% ink) so it never hurts print contrast against the page text.
        backgroundImage:
          `linear-gradient(${roleColorAlpha(theme, 'ink', 0.047)} 1px, transparent 1px),` +
          `linear-gradient(90deg, ${roleColorAlpha(theme, 'ink', 0.047)} 1px, transparent 1px)`,
        backgroundSize: '24px 24px',
        padding: '4px 2px',
        // Fill the full printable A4 box minus the SAME 1mm epsilon the paged
        // deck clamps the outer #cover box to (EditorialMastheadCover DEFECT 1),
        // so the bottom-pinned footer never tips the cover onto a blank page-2.
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm))`,
      }}
    >
      {/* TOP RULE + eyebrow + square brandmark (mockup `.sg-top`). */}
      <div
        className="relative flex items-center justify-between pb-2"
        style={{ borderBottom: `2px solid ${roleColor(theme, 'ink')}` }}
        data-testid="cover-title-block-top"
      >
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: roleColor(theme, 'accent'), fontFamily: theme.bodyFont }}
          data-testid="cover-title-block-eyebrow"
        >
          {eyebrow}
        </span>
        {/* Small square brandmark — accent outline with a solid accent core. */}
        <span
          aria-hidden="true"
          className="relative inline-block"
          data-testid="cover-title-block-mark"
          style={{ width: 16, height: 16, border: `2px solid ${roleColor(theme, 'accent')}` }}
        >
          <span
            className="absolute"
            style={{ inset: 3, background: roleColor(theme, 'accent') }}
          />
        </span>
      </div>
      {/* GATEWAY POWER platform credit — right-aligned under the 2px top rule,
          in the title's fixed 34mm top margin (zero height; the bottom-pinned
          datum footer keeps its fit). */}
      <CoverHeaderPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        fontFamily={theme.bodyFont}
        testId="cover-title-block-platform-credit"
      />

      {/* PROJECT NAME — display grotesque, bold uppercase (mockup `.sg-name`). */}
      <h1
        id="cover-title"
        className="uppercase"
        style={{
          fontFamily: displayFont,
          color: reportHeadingInk(theme),
          fontSize: titleSize,
          fontWeight: 800,
          lineHeight: 1.0,
          letterSpacing: '-0.02em',
          marginTop: '34mm',
          maxWidth: '16ch',
        }}
        data-testid="cover-title-block-name"
        data-cover-site-name-header="true"
      >
        {title}
      </h1>

      {/* ONE-LINE VERDICT — accent on the decision word (mockup `.sg-verdict`).
          Collapses when there is no real verdict. */}
      {hasVerdict && (
        <p
          className="font-semibold mt-3"
          style={{
            color: reportHeadingInk(theme),
            fontFamily: theme.bodyFont,
            fontSize: `calc(${bodyFontSize} * 1.05)`,
            lineHeight: 1.4,
            maxWidth: '60ch',
          }}
          data-testid="cover-title-block-verdict"
        >
          <span
            className="uppercase"
            style={{ color: roleColor(theme, verdictRole) }}
            data-testid="cover-title-block-verdict-word"
          >
            {verdict.label}
          </span>
          {verdictGloss && (
            <span style={{ color: roleColor(theme, 'body') }}> – {verdictGloss}</span>
          )}
        </p>
      )}

      {/* TITLE-BLOCK GRID — bordered grid of fielded cells, ruled dividers
          (mockup `.sg-block`). 3 columns × 2 rows; cells close their right/bottom
          hairline so the lattice is continuous. mono key + mono value. */}
      <div
        className="relative"
        style={{
          marginTop: '14mm',
          border: `1.5px solid ${roleColor(theme, 'ink')}`,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          backgroundColor: roleColorAlpha(theme, 'subtleBg', 0.0),
        }}
        data-testid="cover-title-block-grid"
      >
        {fields.map((field, i) => {
          const isLastCol = (i + 1) % 3 === 0;
          const isLastRow = i >= fields.length - 3;
          return (
            <div
              key={field.id}
              data-testid={`cover-title-block-field-${field.id}`}
              style={{
                borderRight: isLastCol ? undefined : `1px solid ${roleColor(theme, 'hair')}`,
                borderBottom: isLastRow ? undefined : `1px solid ${roleColor(theme, 'hair')}`,
                padding: '9px 12px',
              }}
            >
              <div
                className="uppercase"
                style={{
                  fontFamily: MONO_FONT,
                  fontSize: '7.5px',
                  letterSpacing: '0.16em',
                  color: roleColor(theme, 'muted'),
                }}
              >
                {field.key}
              </div>
              {field.editable && isEditing && onAddressChange ? (
                <input
                  className="mt-1 w-full bg-transparent focus:outline-none focus-visible:ring-1 focus-visible:ring-offset-0"
                  style={{
                    fontFamily: MONO_FONT,
                    fontSize: '11.5px',
                    fontWeight: 500,
                    color: roleColor(theme, 'ink'),
                  }}
                  value={editedAddress ?? (locationDatum ?? '')}
                  onChange={(e) => onAddressChange(e.target.value)}
                  data-testid="cover-title-block-location-input"
                />
              ) : (
                <div
                  className="mt-1"
                  style={{
                    fontFamily: MONO_FONT,
                    fontSize: '11.5px',
                    fontWeight: 500,
                    color: roleColor(theme, 'ink'),
                  }}
                >
                  {field.value}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BOUNDARY DIAGRAM INSET (mockup `.sg-svg`) — the shared keyless overlay,
          rendered small. No geometry → a labelled empty datum line. */}
      <div className="relative" style={{ marginTop: '12mm' }} data-testid="cover-title-block-diagram">
        {hasGeometry ? (
          <div className="pointer-events-none" style={{ height: 88 }}>
            <HeroCoverBoundaryOverlay
              report={report}
              layers={layers}
              layerLabels={effectiveBranding.layerIndicators}
              textTone="on-light"
              strokeColor={roleColor(theme, 'accent')}
              strokeScale={0.85}
              className="h-full w-full max-w-none aspect-auto"
            />
          </div>
        ) : (
          <div
            className="uppercase"
            style={{
              fontFamily: MONO_FONT,
              fontSize: '9px',
              letterSpacing: '0.1em',
              color: roleColor(theme, 'muted'),
              paddingTop: 8,
              borderTop: `1px solid ${roleColor(theme, 'hair')}`,
            }}
            data-testid="cover-title-block-diagram-empty"
          >
            Site boundary – geometry not available
            {acres != null && (
              <span style={{ color: roleColor(theme, 'faint') }}>
                {' · '}{Math.round(acres)} ac
              </span>
            )}
          </div>
        )}
      </div>

      {/* DEFECT 8 (parity) — compact on-cover index, drawn as a hairline
          title-block continuation grid. Replaces the standalone TOC page when the
          caller threads ≥3 entries (it suppresses that page via coverCarriesToc).
          The <3-entry guard mirrors EditorialMastheadCover. */}
      {carriesToc && (
        <div className="relative" style={{ marginTop: '10mm' }} data-testid="cover-title-block-toc">
          <div
            className="flex items-baseline justify-between uppercase pb-1.5"
            style={{
              fontFamily: MONO_FONT,
              fontSize: '7.5px',
              letterSpacing: '0.14em',
              color: roleColor(theme, 'accent'),
              borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
            }}
          >
            <span>Contents</span>
            <span
              style={{ color: roleColorAlpha(theme, 'ink', 0.45) }}
              data-testid="cover-title-block-toc-count"
            >
              {tocEntries!.length} sheets
            </span>
          </div>
          <div
            data-testid="cover-title-block-toc-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              borderLeft: `1px solid ${roleColorAlpha(theme, 'ink', 0.1)}`,
              marginTop: 8,
            }}
          >
            {tocEntries!.map((entry, i) => {
              const page = tocPageNumbers?.[entry.id];
              const hasPage = typeof page === 'number' && Number.isFinite(page);
              return (
                <div
                  key={entry.id}
                  className="relative"
                  data-testid={`cover-title-block-toc-entry-${entry.id}`}
                  style={{
                    borderRight: `1px solid ${roleColorAlpha(theme, 'ink', 0.1)}`,
                    borderBottom: `1px solid ${roleColorAlpha(theme, 'ink', 0.1)}`,
                    padding: '8px 11px 9px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  {hasPage && (
                    <span
                      className="absolute tabular-nums"
                      style={{
                        top: 7,
                        right: 10,
                        fontFamily: MONO_FONT,
                        fontSize: '9px',
                        fontWeight: 500,
                        color: roleColorAlpha(theme, 'ink', 0.45),
                      }}
                      data-testid={`cover-title-block-toc-page-${entry.id}`}
                    >
                      {formatPageNumber(page!, tocFmt)}
                    </span>
                  )}
                  <span
                    className="tabular-nums"
                    style={{
                      fontFamily: MONO_FONT,
                      fontSize: '8px',
                      fontWeight: 600,
                      letterSpacing: '0.16em',
                      color: roleColor(theme, 'accent'),
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <a
                    href={`#${entry.id}`}
                    style={{
                      fontFamily: theme.headingFont,
                      fontSize: '11px',
                      lineHeight: 1.2,
                      color: roleColor(theme, 'body'),
                      textDecoration: 'none',
                    }}
                  >
                    {entry.label}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Audience chrome — kept in the contract so the dispatch site need not
          fork (PreparedForLine is a no-op today but honours the picker props). */}
      <PreparedForLine
        report={report}
        isAdmin={isAdmin}
        pickerOpen={pickerOpen}
        onTogglePicker={onTogglePicker}
        onSelectArchetype={onSelectArchetype}
      />

      {/* MONO DATUM-STRIP FOOTER (mockup `.sg-foot`) — bottom-pinned via mt-auto.
          Left: org / engine slug. Right: generation date. A hairline top rule
          separates it from the block above. The DISTINCT archetype disclaimer (if
          any) sits above the fixed beta caveat. */}
      <div className="mt-auto" data-testid="cover-title-block-footer">
        <div
          className="flex items-baseline justify-between uppercase pt-2"
          style={{
            fontFamily: MONO_FONT,
            fontSize: '8px',
            letterSpacing: '0.08em',
            color: roleColor(theme, 'muted'),
            borderTop: `1px solid ${roleColor(theme, 'hair')}`,
          }}
        >
          <span data-testid="cover-title-block-footer-slug">
            {(theme.companyName?.trim() || 'Datacrate')} · Engineering + Decision Intelligence
          </span>
          {genDate && (
            <span className="tabular-nums" data-testid="cover-title-block-footer-date">{genDate}</span>
          )}
        </div>
        {isDistinctDisclaimer(disclaimer) && (
          <div
            className="text-[8px] leading-relaxed mt-1.5"
            style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
            data-testid="cover-title-block-archetype-disclaimer"
          >
            {disclaimer}
          </div>
        )}
        <div
          className="text-[8px] leading-relaxed mt-1"
          style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          data-testid="cover-title-block-beta-disclaimer"
        >
          Confidential. Beta workflow: directional guidance only; may contain errors.
          Independently validate all findings with qualified advisors before any decision.
        </div>
      </div>
    </div>
  );
};
